import React, { useEffect, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, A11y } from "swiper/modules";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchMenuList, fetchBanners, fetchCafeList, fetchSoldOutItems } from "../api";
import { getBranchByCode } from "../indexedDBUtils";
import { fetchBranchHours } from "../services/branchHours";
import { formatStoreTime, getOrderingAvailability } from "../utils/storeHours";
import { useCart } from "../context/CartContext";
import CategorySection from "./CategorySection";
import ProductDetailModal from "./ProductDetailModal";
import CartSheet from "./CartSheet";
import CartPage from "./CartPage";
import CustomizationChooserSheet from "./CustomizationChooserSheet";
import CachedVideo from "./CachedVideo";
import CachedImage from "./CachedImage";
import "./MenuList.css";
import "swiper/css";
import "swiper/css/pagination";

const LOADING_PHRASES = [
  "Adding the boba pearls!",
  "Whisking the matcha!",
  "Mixing your favorites!",
  "Pouring the perfect blend!",
  "Sweetening the selections!",
  "Shaking up your menu!",
  "Sealing your cup!",
  "Brewing something delicious!",
];

export default function MenuList({ selectedCafe, onBackToCafes, showCafeSelector = false, onClearCafe }) {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPhrase, setLoadingPhrase] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openMode, setOpenMode] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSearchSticky, setIsSearchSticky] = useState(false);
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [banners, setBanners] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [customizationProduct, setCustomizationProduct] = useState(null);
  const [initialCartItem, setInitialCartItem] = useState(null);
  const [searchClosing, setSearchClosing] = useState(false);
  const [searchPlaceholder, setSearchPlaceholder] = useState(0);
  const [showCartPage, setShowCartPage] = useState(false);
  const [loadedVideos, setLoadedVideos] = useState({});
  const [branchName, setBranchName] = useState(null);
  const [branchPhone, setBranchPhone] = useState(null);
  const [branchConfig, setBranchConfig] = useState(null);
  const [branchHours, setBranchHours] = useState(null);
  const [isRefreshingHours, setIsRefreshingHours] = useState(false);
  const [soldOutItems, setSoldOutItems] = useState({});
  const [hideHeader, setHideHeader] = useState(false);
  const sectionRefs = useRef({});
  const subcategoryRefs = useRef({});
  const searchBarRef = useRef(null);
  const filterBarRef = useRef(null);
  const originalSearchPositionRef = useRef(0);
  const originalFilterPositionRef = useRef(0);

  const urlParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : null;
  const urlCafeCode = urlParams?.get("cafeCode");
  const hideHeaderParam = urlParams?.get("hideHeader") === "true";
  const activeCafeCode = String(urlCafeCode || selectedCafe?.ristaId || selectedCafe?.cafeCode || "default")
    .trim()
    .toUpperCase();

  const orderingAvailability = getOrderingAvailability({
    orderingEnabled: branchConfig?.orderingEnabled,
    startTime: branchHours?.startTime,
    endTime: branchHours?.endTime,
  });
  const isOrderingEnabled = orderingAvailability.orderingEnabled;
  const isViewOnly = !isOrderingEnabled;
  const isStoreClosed = orderingAvailability.timingIsValid && orderingAvailability.storeIsOpen === false;

  // Cart state lives in CartContext (Firestore-backed) — pull the helpers we need.
  const {
    items: cartItems,
    addToCart,
    updateQuantity,
    replaceCartItem,
    decreaseFromCart,
    adjustCartSelectionQuantity,
    getProductCartQty,
    getCartQtyForSelection,
    setTaxRates,
    setCafeDetails,
    itemCount: cartItemCount,
    subtotal: cartSubtotal,
    taxTotal: cartTaxTotal,
    grandTotal: cartGrandTotal,
  } = useCart();

  // Generate dynamic search placeholder texts from actual product names
  const getSearchPlaceholders = () => {
    const defaultPlaceholders = ["Search menu items..."];

    if (productNames.length === 0) {
      return defaultPlaceholders;
    }

    // Shuffle and pick random product names
    const shuffled = [...productNames].sort(() => Math.random() - 0.5);
    const selectedProducts = shuffled.slice(0, 5); // Pick 5 random products

    const dynamicPlaceholders = selectedProducts.map(name => {
      // Remove anything in brackets ([], ())
      const sanitized = name.replace(/\[.*?\]|\(.*?\)/g, '').trim();
      return `Search for '${sanitized}'`;
    });

    return [...defaultPlaceholders, ...dynamicPlaceholders];
  };

  const searchPlaceholders = getSearchPlaceholders();

  // Highlight matched words in text
  const highlightText = (text, searchTerm) => {
    if (!text || !searchTerm || searchTerm.trim() === '') return text;

    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    // Create a regex pattern that matches any of the search words (case insensitive)
    const pattern = searchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    // Split text by matches and wrap matches in <strong> tags
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          // Check if this part matches any search word (case-insensitive)
          const isMatch = searchWords.some(word => part.toLowerCase() === word);
          // Only wrap matched text in strong tags, return others as plain strings
          return isMatch ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </>
    );
  };

  useEffect(() => {
    // Rotate loading phrases
    const phraseInterval = setInterval(() => {
      setLoadingPhrase((prev) => (prev + 1) % LOADING_PHRASES.length);
    }, 2000); // Change phrase every 2 seconds

    return () => clearInterval(phraseInterval);
  }, []);

  // Refresh branch details from the backend so orderingEnabled changes are live.
  useEffect(() => {
    const loadBranchName = async () => {
      if (!selectedCafe) {
        setBranchName(null);
        setBranchPhone(null);
        setBranchConfig(null);
        return;
      }

      const branchCode = selectedCafe.ristaId || selectedCafe.cafeCode;
      if (!branchCode) {
        setBranchName(null);
        setBranchPhone(selectedCafe?.phone || null);
        setBranchConfig(null);
        return;
      }

      try {
        const branches = await fetchCafeList();
        const branch = (branches || []).find((item) => {
          const code = item?.branchCode || item?.ristaId || item?.cafeCode;
          return String(code || '').trim().toUpperCase() === String(branchCode).trim().toUpperCase();
        });
        if (branch) {
          setBranchName(branch.name);
          setBranchPhone(branch.phone || selectedCafe?.phone || null);
          setBranchConfig(branch);
        } else {
          setBranchPhone(selectedCafe?.phone || null);
          setBranchConfig(null);
        }
      } catch (error) {
        console.error('Error loading branch name:', error);
        const cachedBranch = await getBranchByCode(branchCode);
        if (cachedBranch) {
          setBranchName(cachedBranch.name);
          setBranchPhone(cachedBranch.phone || selectedCafe?.phone || null);
          setBranchConfig(cachedBranch);
        } else {
          setBranchPhone(selectedCafe?.phone || null);
          setBranchConfig(null);
        }
      }
    };

    loadBranchName();
  }, [selectedCafe]);

  const refreshBranchHours = async () => {
    try {
      const hours = await fetchBranchHours(activeCafeCode);
      setBranchHours(hours);
      return hours;
    } catch (error) {
      // Missing or unreadable timings retain the existing orderingEnabled behavior.
      console.error("Error loading branch hours:", error);
      setBranchHours(null);
      return null;
    }
  };

  const handleRefreshBranchHours = async () => {
    setIsRefreshingHours(true);
    await refreshBranchHours();
    setIsRefreshingHours(false);
  };

  useEffect(() => {
    let isActive = true;

    const loadBranchHours = async () => {
      try {
        const hours = await fetchBranchHours(activeCafeCode);
        if (isActive) setBranchHours(hours);
      } catch (error) {
        console.error("Error loading branch hours:", error);
        if (isActive) setBranchHours(null);
      }
    };

    setBranchHours(null);
    loadBranchHours();
    return () => { isActive = false; };
  }, [activeCafeCode]);

  useEffect(() => {
    if (isStoreClosed) setShowCartPage(false);
  }, [isStoreClosed]);

  // Keep the cart context in sync with the resolved branch name / phone so the
  // checkout can label the order correctly.
  useEffect(() => {
    setCafeDetails({
      branchName: branchName || selectedCafe?.name || store?.name || "",
      branchPhone: branchPhone || selectedCafe?.phone || "",
    });
  }, [branchName, branchPhone, selectedCafe, store, setCafeDetails]);

  useEffect(() => {
    // Track loading start time for minimum display duration
    const loadingStartTime = Date.now();
    const MIN_LOADING_TIME = 3000; // 3 seconds

    // Get cafeCode from URL query params first, then fall back to selectedCafe
    const cafeCode = activeCafeCode || "0";
    setHideHeader(hideHeaderParam);

    fetchMenuList(cafeCode).then((data) => {
      setCategories(data.categories || []);
      setTags(data.tags || []);
      setProductNames(data.productNames || []);
      setStore(data.store || null);
      const taxMap = {};
      (data.taxTypes || []).forEach((taxType) => {
        if (taxType?.taxTypeId) {
          taxMap[taxType.taxTypeId] = {
            name: taxType.name || taxType.taxName || taxType.title || "Tax",
            percentage: Number(taxType.percentage || 0),
          };
        }
      });
      setTaxRates(taxMap);

      // Fetch sold-out items after menu is loaded
      const branchCode = activeCafeCode || "CA-096";
      fetchSoldOutItems(branchCode).then((soldOutMap) => {
        setSoldOutItems(soldOutMap);
      });

      // Calculate elapsed time and wait if needed
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }).catch((error) => {
      console.error("Error loading menu:", error);

      // Even on error, respect minimum loading time
      const elapsedTime = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    });

    const handleScroll = () => {
      if (!searchMode) {
        setShowScrollTop(window.scrollY > 300);

        if (searchBarRef.current && originalSearchPositionRef.current === 0) {
          originalSearchPositionRef.current =
            searchBarRef.current.getBoundingClientRect().top + window.scrollY;
        }

        if (originalSearchPositionRef.current > 0) {
          setIsSearchSticky(window.scrollY >= originalSearchPositionRef.current);
        }

        // Handle filter bar stickiness
        if (filterBarRef.current && originalFilterPositionRef.current === 0) {
          originalFilterPositionRef.current =
            filterBarRef.current.getBoundingClientRect().top + window.scrollY;
        }

        if (originalFilterPositionRef.current > 0) {
          // Make filter sticky when search is sticky and filter scrolls to top
          const searchHeight = isSearchSticky ? (searchBarRef.current?.offsetHeight || 0) : 0;
          setIsFilterSticky(window.scrollY >= originalFilterPositionRef.current - searchHeight);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    setTimeout(() => {
      if (searchBarRef.current) {
        originalSearchPositionRef.current =
          searchBarRef.current.getBoundingClientRect().top + window.scrollY;
      }
      if (filterBarRef.current) {
        originalFilterPositionRef.current =
          filterBarRef.current.getBoundingClientRect().top + window.scrollY;
      }
    }, 100);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeCafeCode, hideHeaderParam, searchMode, selectedCafe]); // Add selectedCafe and hideHeader as dependency

  // Reset position refs when hideHeader changes so they get recalculated
  useEffect(() => {
    originalSearchPositionRef.current = 0;
    originalFilterPositionRef.current = 0;
  }, [hideHeader]);

  useEffect(() => {
    let isActive = true;

    const loadBanners = async () => {
      // Use store.id from the menu response, or fall back to selectedCafe
      const cafeCode = selectedCafe?.ristaId || selectedCafe?.cafeCode || "0";

      try {
        const fetchedBanners = await fetchBanners({
          cafeCode,
        });

        if (!isActive) {
          return;
        }

        const validBanners = (fetchedBanners || []).filter(
          (banner) => banner.mediaUrl && banner.mediaType !== "unknown"
        );

        setBanners(validBanners);

        // Extract video URLs for prefetching
        const videoUrls = validBanners
          .filter(banner => banner.mediaType === "video")
          .map(banner => banner.mediaUrl);

        // Extract image URLs for prefetching
        const imageUrls = validBanners
          .filter(banner => banner.mediaType === "image")
          .map(banner => banner.mediaUrl);

        // Prefetch videos in the background
        if (videoUrls.length > 0) {
          import("../utils/videoCacheService").then(({ default: videoCacheService }) => {
            videoCacheService.init().then(() => {
              videoCacheService.prefetchVideos(videoUrls);
            });
          });
        }

        // Prefetch images in the background
        if (imageUrls.length > 0) {
          import("../utils/imageCacheService").then(({ default: imageCacheService }) => {
            imageCacheService.init().then(() => {
              imageCacheService.prefetchImages(imageUrls);
            });
          });
        }
      } catch (error) {
        if (isActive) {
          setBanners([]);
        }
      }
    };

    loadBanners();

    return () => {
      isActive = false;
    };
  }, [store, selectedCafe]);

  // Animate search placeholder text
  useEffect(() => {
    if (searchPlaceholders.length === 0) return;

    const interval = setInterval(() => {
      setSearchPlaceholder((prev) => (prev + 1) % searchPlaceholders.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [searchPlaceholders.length, productNames.length]);

  // Swiper handles banner autoplay and looping.

  const scrollToCategory = (id, subcategoryName = null, isRetry = false) => {
    // If subcategoryName is provided, try to scroll to that subcategory
    let element = null;
    if (subcategoryName) {
      const subcategoryKey = `${id}-${subcategoryName}`;
      element = subcategoryRefs.current[subcategoryKey];
    }

    // Fall back to category if subcategory not found
    if (!element) {
      element = sectionRefs.current[id];
    }

    if (element) {
      // Always account for both sticky bars if filter exists
      // Categories are always below the filter bar, so both will be sticky at destination
      const searchHeight = searchBarRef.current?.offsetHeight || 0;
      const filterHeight = filterBarRef.current?.offsetHeight || 0;

      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetTop = elementTop - searchHeight - filterHeight - 10;

      window.scrollTo({ top: offsetTop, behavior: "smooth" });

      // Retry once after layout has settled to correct position
      if (!isRetry) {
        setTimeout(() => {
          scrollToCategory(id, subcategoryName, true);
        }, 400);
      }
    }
    setMobileMenuOpen(false);
  };

  const handleProductClick = (product, options) => {
    if ((options?.source === 'add' || options?.source === 'customization') && hasConfigurableOptions(product)) {
      const existingItems = getCartItemsForProduct(product);
      if (existingItems.length > 0) {
        setCustomizationProduct(product);
        setSelectedProduct(null);
        setOpenMode(null);
        setInitialCartItem(null);
        return;
      }
    }

    setSelectedProduct(product);
    setOpenMode(options?.source === 'add' ? 'add' : null);
    setInitialCartItem(null);
  };

  const shouldOpenCartAfterAdd = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 769px)").matches;
  };

  const handleAddToCart = async (...args) => {
    const latestHours = await refreshBranchHours();
    const latestAvailability = getOrderingAvailability({
      orderingEnabled: branchConfig?.orderingEnabled,
      startTime: latestHours?.startTime,
      endTime: latestHours?.endTime,
    });

    if (!latestAvailability.orderingEnabled) {
      setShowCartPage(false);
      return;
    }

    addToCart(...args);
    if (shouldOpenCartAfterAdd()) {
      setShowCartPage(true);
    }
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setOpenMode(null);
    setInitialCartItem(null);
  };
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const hasConfigurableOptions = (product) => {
    const variationGroups = Array.isArray(product?.variations) ? product.variations : [];

    const hasMultipleVariants = variationGroups.some(
      (group) => group?.isVariantGroup && (group?.options?.length || 0) > 1
    );

    const hasOptionSetsOnProduct = variationGroups.some(
      (group) => !group?.isVariantGroup && (group?.options?.length || 0) > 0
    );

    const hasOptionSetsOnVariants = variationGroups.some(
      (group) => group?.isVariantGroup
        && (group?.options || []).some(
          (variant) => Array.isArray(variant?.optionSets)
            && variant.optionSets.some((set) => (set?.options?.length || 0) > 0)
        )
    );

    return hasMultipleVariants || hasOptionSetsOnProduct || hasOptionSetsOnVariants;
  };

  const getCartItemsForProduct = (product) => {
    const key = product?.id || product?.skuCode || product?.name;
    if (!key) {
      return [];
    }
    return (cartItems || []).filter((item) => item.productId === key);
  };

  const getProductCartLineCount = (product) => getCartItemsForProduct(product).length;

  const findProductForCartItem = (item) => {
    if (!item) return null;
    for (const category of categories) {
      const product = (category.products || []).find((candidate) => {
        const productKey = candidate?.id || candidate?.skuCode || candidate?.name;
        return (
          productKey === item.productId ||
          candidate?.id === item.productId ||
          candidate?.skuCode === item.skuCode ||
          candidate?.skuCode === item.productId ||
          candidate?.name === item.productName
        );
      });
      if (product) return product;
    }
    return null;
  };

  const closeCustomizationChooser = () => {
    setCustomizationProduct(null);
  };

  const handleAddNewCustomization = () => {
    setSelectedProduct(customizationProduct);
    setOpenMode('add');
    setInitialCartItem(null);
    setCustomizationProduct(null);
  };

  const handleEditCustomization = (item) => {
    setSelectedProduct(customizationProduct);
    setOpenMode('add');
    setInitialCartItem(item);
    setCustomizationProduct(null);
  };

  const handleEditCartItem = (item) => {
    const product = findProductForCartItem(item);
    if (!product) return;
    setSelectedProduct(product);
    setOpenMode('add');
    setInitialCartItem(item);
    setCustomizationProduct(null);
  };

  const canEditCartItem = (item) => {
    const product = findProductForCartItem(item);
    return hasConfigurableOptions(product);
  };

  const handleSearchClick = () => {
    setSearchMode(true);
  };

  const handleBackFromSearch = () => {
    setSearchClosing(true);
    setTimeout(() => {
      setSearchMode(false);
      setSearchTerm("");
      setSearchClosing(false);
    }, 300); // Match animation duration
  };

  // Handle back to cafe selection
  const handleBackToCafes = () => {
    if (onBackToCafes) {
      onBackToCafes();
    }
  };

  // Toggle accordion for categories
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Handle video loaded
  const handleVideoLoaded = (bannerId) => {
    // Temporarily disabled for testing
    setLoadedVideos((prev) => ({
      ...prev,
      [bannerId]: true,
    }));
  };

  // Handle banner click
  const handleBannerClick = (banner) => {
    if (!banner.redirectMode) return;

    const redirectMode = banner.redirectMode.toLowerCase();

    if (redirectMode === 'category') {
      // Scroll to category by matching category name (temporary fix for ID mismatch)
      if (banner.category && banner.category.categoryName) {
        const categoryName = banner.category.categoryName.toLowerCase().trim();
        // Find the category by name
        const foundCategory = categories.find(cat =>
          cat.parentName?.toLowerCase().trim() === categoryName
        );
        if (foundCategory) {
          const subcategoryName = banner.subCategory?.categoryName || null;
          scrollToCategory(foundCategory.id, subcategoryName);
        }
      }
    } else if (redirectMode === 'product') {
      // Open product modal using ristaSkuCode
      if (banner.product && banner.product.variations && banner.product.variations.length > 0) {
        const ristaSkuCode = banner.product.variations[0]?.masterProduct?.ristaSkuCode;
        if (ristaSkuCode) {
          // Find the product in categories by matching skuCode
          let foundProduct = null;
          for (const category of categories) {
            foundProduct = category.products.find(p => p.skuCode === ristaSkuCode);
            if (foundProduct) break;
          }
          if (foundProduct) {
            setSelectedProduct(foundProduct);
          }
        }
      }
    }
  };

  // Handle tag selection toggle with veg/non-veg mutual exclusivity
  // Helper function to check if a product should be hidden due to sold-out variants
  const isProductSoldOut = (product, soldOutItems) => {
    if (!soldOutItems || Object.keys(soldOutItems).length === 0) {
      return false;
    }

    // First check if the product itself is sold out
    if (product.skuCode && product.skuCode in soldOutItems) {
      return true;
    }

    // Check if product has variants
    const variantGroup = product.variations?.find(v => v.isVariantGroup);
    if (!variantGroup || !variantGroup.options || variantGroup.options.length === 0) {
      // No variants, only check product-level skuCode (already checked above)
      return false;
    }

    // If there's only one variant and it's sold out, hide the product
    if (variantGroup.options.length === 1) {
      const singleVariant = variantGroup.options[0];
      if (singleVariant.skuCode && singleVariant.skuCode in soldOutItems) {
        return true;
      }
    }

    // If there are multiple variants, check if ALL are sold out
    if (variantGroup.options.length > 1) {
      const allVariantsSoldOut = variantGroup.options.every(option => 
        option.skuCode && option.skuCode in soldOutItems
      );
      if (allVariantsSoldOut) {
        return true;
      }
    }

    return false;
  };

  // Helper: hide "free" items (₹0) across browse/search.
  // A product is considered free ONLY if every selectable configuration is free.
  const isProductCompletelyFree = (product) => {
    const numericPrices = [];

    const maybePushPrice = (val) => {
      const n = Number(val);
      if (Number.isFinite(n)) numericPrices.push(n);
    };

    // Base display price (Simple: base price, Group: cheapest variant)
    maybePushPrice(product?.price);

    // Product-level option sets OR variant group options
    const variationGroups = product?.variations || [];
    variationGroups.forEach((group) => {
      (group?.options || []).forEach((opt) => {
        maybePushPrice(opt?.price);

        // For variant options, we may have nested option sets
        const nestedOptionSets = opt?.optionSets || [];
        nestedOptionSets.forEach((nestedGroup) => {
          (nestedGroup?.options || []).forEach((nestedOpt) => {
            maybePushPrice(nestedOpt?.price);
          });
        });
      });
    });

    // If we couldn't confidently determine pricing, don't hide it.
    if (numericPrices.length === 0) return false;

    return numericPrices.every((p) => p <= 0);
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags((prev) => {
      // Find the clicked tag
      const clickedTag = tags.find(t => t.id === tagId);
      const clickedTagName = clickedTag?.name?.toLowerCase() || '';

      // Check if this is a veg or non-veg tag
      const isVegTag = clickedTagName.includes('veg') && !clickedTagName.includes('non');
      const isNonVegTag = clickedTagName.includes('non') && clickedTagName.includes('veg');

      // If clicking veg or non-veg, find and remove the opposite one
      if (isVegTag || isNonVegTag) {
        // Find the opposite tag's ID
        const oppositeTag = tags.find(t => {
          const tagName = t.name?.toLowerCase() || '';
          if (isVegTag) {
            return tagName.includes('non') && tagName.includes('veg');
          } else {
            return tagName.includes('veg') && !tagName.includes('non');
          }
        });

        // Remove opposite tag if it exists in selected tags
        const withoutOpposite = oppositeTag
          ? prev.filter((id) => id !== oppositeTag.id)
          : prev;

        // Toggle the clicked tag
        const newTags = withoutOpposite.includes(tagId)
          ? withoutOpposite.filter((id) => id !== tagId)
          : [...withoutOpposite, tagId];

        console.log('Selected tags:', newTags);
        return newTags;
      }

      // For other tags, just toggle normally
      const newTags = prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId];
      console.log('Selected tags:', newTags);
      return newTags;
    });
  };

  const filteredCategories = categories
    .map((cat) => {
      const searchLower = searchTerm.toLowerCase().trim();
      // In search mode, ignore tag filters (use empty array)
      const activeTagFilters = searchMode ? [] : selectedTags;

      // If search is empty, return all products (with tag filter only)
      if (!searchLower) {
        return {
          ...cat,
          products: cat.products.filter((p) => {
            // Filter out sold-out items (including products with all variants sold out)
            const isNotSoldOut = !isProductSoldOut(p, soldOutItems);
            // Filter out completely-free items (₹0)
            const isNotFree = !isProductCompletelyFree(p);
            const matchesTags = activeTagFilters.length === 0 ||
              (p.tags && p.tags.some((tag) => activeTagFilters.includes(tag.id)));
            return isNotSoldOut && isNotFree && matchesTags;
          }),
        };
      }

      // Split search into words
      const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

      // Helper function: ALL search words must be found in the text
      const fuzzyMatch = (text) => {
        if (!text) return false;
        const textLower = text.toLowerCase();
        // Every search word must be found somewhere in the text
        return searchWords.every(word => textLower.includes(word));
      };

      // Check if category name matches search
      const categoryMatches = fuzzyMatch(cat.parentName);

      // Get list of matching subcategory names
      const matchingSubcategories = cat.subCategories?.filter(subCat =>
        fuzzyMatch(subCat.name)
      ).map(subCat => subCat.name) || [];

      const hasSubcategoryMatch = matchingSubcategories.length > 0;

      // If category or subcategory matches, include all products (with tag filter)
      // Otherwise, filter products by name
      return {
        ...cat,
        products: cat.products.filter((p) => {
          // Filter out sold-out items (including products with all variants sold out)
          const isNotSoldOut = !isProductSoldOut(p, soldOutItems);
          // Filter out completely-free items (₹0)
          const isNotFree = !isProductCompletelyFree(p);
          // Tag filtering: if any tags are selected, product must have at least one of them
          const matchesTags = activeTagFilters.length === 0 ||
            (p.tags && p.tags.some((tag) => activeTagFilters.includes(tag.id)));

          // If category name matches, show all products in category
          if (categoryMatches) {
            return isNotSoldOut && isNotFree && matchesTags;
          }

          // If subcategory matches, only show products from that subcategory
          if (hasSubcategoryMatch) {
            const productInMatchingSubcategory = matchingSubcategories.includes(p.subCategoryName);
            return isNotSoldOut && isNotFree && productInMatchingSubcategory && matchesTags;
          }

          // Otherwise, check product name OR description with fuzzy matching AND tags
          const productNameMatches = fuzzyMatch(p.name);
          const descriptionMatches = fuzzyMatch(p.description || p.desc || '');

          return isNotSoldOut && isNotFree && (productNameMatches || descriptionMatches) && matchesTags;
        }),
      };
    })
    .filter((cat) => cat.products.length > 0);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="coffee-loader"></div>
        <p key={loadingPhrase} className="loading-text-animated">
          {LOADING_PHRASES[loadingPhrase]}
        </p>
      </div>
    );
  }

  // Search Mode View
  if (searchMode) {
    return (
      <div className={`search-mode-container ${searchClosing ? 'closing' : ''}`}>
        <div className="search-mode-header">
          <button className="back-button" onClick={handleBackFromSearch}>
            ↓
          </button>
          <div className="search-mode-bar-container">
            <input
              type="text"
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-mode-bar"
              autoFocus
            />
            {searchTerm && (
              <button
                className="clear-search-btn-mode"
                onClick={() => setSearchTerm("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="search-results-container">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((cat) => (
              <div key={cat.id}>
                <CategorySection
                  category={cat}
                  tags={tags}
                  onProductClick={handleProductClick}
                  onAddToCart={isViewOnly ? null : handleAddToCart}
                  onDecreaseFromCart={isViewOnly ? null : decreaseFromCart}
                  getProductCartQty={getProductCartQty}
                  getProductCartLineCount={getProductCartLineCount}
                  subcategoryRefs={subcategoryRefs}
                  highlightText={highlightText}
                  searchTerm={searchTerm}
                  soldOutItems={soldOutItems}
                  isViewOnly={isViewOnly}
                />
              </div>
            ))
          ) : (
            <p className="no-products-message">No products found</p>
          )}
        </div>

        {selectedProduct && (
          <ProductDetailModal
            product={selectedProduct}
            onClose={closeModal}
            onAddToCart={isViewOnly ? null : handleAddToCart}
            onReplaceCartItem={isViewOnly ? null : replaceCartItem}
            onAdjustCartSelection={isViewOnly ? null : adjustCartSelectionQuantity}
            getCartQtyForSelection={getCartQtyForSelection}
            soldOutItems={soldOutItems}
            compactMode={openMode === 'add'}
            initialCartItem={initialCartItem}
            isViewOnly={isViewOnly}
          />
        )}

        {customizationProduct && getCartItemsForProduct(customizationProduct).length > 0 && (
          <CustomizationChooserSheet
            product={customizationProduct}
            cartItems={getCartItemsForProduct(customizationProduct)}
            onClose={closeCustomizationChooser}
            onAddNew={handleAddNewCustomization}
            onEdit={handleEditCustomization}
            onUpdateQuantity={updateQuantity}
          />
        )}

        {!isViewOnly && <CartSheet onViewCart={() => setShowCartPage(true)} />}

        {showCartPage && (
          <CartPage
            onBack={() => setShowCartPage(false)}
            onEditItem={handleEditCartItem}
            canEditItem={canEditCartItem}
          />
        )}
      </div>
    );
  }

  // Get cafe name for display - prioritize branch name from IndexedDB, then store from API response
  const getCafeName = () => {
    // Use branch name from IndexedDB (fetched via branchCode)
    if (branchName) {
      return branchName;
    }
    // Use store name from menu API response if available
    if (store?.name) {
      return store.name;
    }
    // Fall back to default
    return 'Got Tea - Location';
  };

  const getCafePhone = () => (
    branchPhone
    || selectedCafe?.phone
    || store?.phone
    || store?.phoneNumber
    || store?.contactNumber
    || store?.mobile
    || ""
  );

  // Normal View
  return (
    <div className="menu-container">
      {/* Background Art - positioned behind everything */}
      {/* <img
        src="/BTCArtSmall.png"
        alt=""
        className="header-background-art"
        aria-hidden="true"
      /> */}

      {!hideHeader && (
        <div className="menu-top-section">
          <div className="menu-header">
            <div className="menu-left">
              {/* Back to cafes button (optional) */}
              {onBackToCafes && (
                <button className="back-to-cafes-btn" onClick={handleBackToCafes}>
                  ←
                </button>
              )}
              <div className="menu-logo-title">
                <img
                  src="/GotTeaLogo.png"
                  alt="Got Tea Logo"
                  className="menu-logo"
                />
                <div className="menu-text">
                  <h1>{getCafeName()}</h1>
                  {getCafePhone() && (
                    <p className="menu-phone">
                      <a href={`tel:${String(getCafePhone()).replace(/\s+/g, "")}`}>
                        <PhoneIcon style={{ fontSize: 14 }} />
                        {getCafePhone()}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
            {showCafeSelector && (
              <div className="menu-right">
                <button className="select-cafe-btn" onClick={onClearCafe}>
                  Select Café
                </button>
              </div>
            )}
          </div>

          <div
            ref={searchBarRef}
            className={`search-bar-container ${isSearchSticky ? "sticky" : ""}`}
            onClick={handleSearchClick}
          >
            <div className="search-bar">
              <span key={searchPlaceholder} className="search-text-animated">
                {searchPlaceholders[searchPlaceholder]}
              </span>
            </div>
          </div>
        </div>
      )}

      {!hideHeader && isSearchSticky && <div className="search-spacer"></div>}

      {/* Top spacer when header is hidden */}
      {hideHeader && <div style={{ height: '20px' }}></div>}

      {/* Banner Section */}
      {banners.length > 0 && (
        <div className="banner-section">
          <Swiper
             slidesPerView={'auto'}
             modules={[Autoplay, A11y]}
             loop
             autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
             pagination={{ clickable: true }}
             centeredSlides={true}
             spaceBetween={12}
             grabCursor
             className="banner-swiper"
             breakpoints={{
               700: {
                 slidesPerView: 2,
                 spaceBetween:22,
                 centeredSlides: false,
               },
               1024: {
                 slidesPerView: 3,
                 spaceBetween: 20,
                 centeredSlides: false,
               },
             }}
          >
            {banners.map((banner) => (
              <SwiperSlide
                key={banner.id}
                data-swiper-autoplay={(banner.duration || 5) * 1000}
              >
                <div
                  className="banner-card"
                  onClick={() => handleBannerClick(banner)}
                  style={{ cursor: (banner.redirectMode === 'product' || banner.redirectMode === 'category') ? 'pointer' : 'default' }}
                >
                  {banner.mediaType === "video" ? (
                    <>
                      {!loadedVideos[banner.id] && (
                        <div className="banner-video-skeleton">
                          <div className="skeleton-shimmer"></div>
                        </div>
                      )}
                      <CachedVideo
                        className="banner-video"
                        src={banner.mediaUrl}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                        onLoadedData={() => handleVideoLoaded(banner.id)}
                        style={{
                          opacity: loadedVideos[banner.id] ? 1 : 0,
                          visibility: loadedVideos[banner.id] ? 'visible' : 'hidden'
                        }}
                      />
                    </>
                  ) : (
                    <CachedImage
                      src={banner.mediaUrl}
                      alt={banner.title || banner.alt || "Promotional banner"}
                      className="banner-image"
                    />
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Dynamic Tag Filters */}
      {tags.length > 0 && (() => {
        // Separate veg/non-veg tags from other tags
        const vegTag = tags.find(t => {
          const name = t.name?.toLowerCase() || '';
          return name.includes('veg') && !name.includes('non');
        });
        const nonVegTagDef = tags.find(t => {
          const name = t.name?.toLowerCase() || '';
          return name.includes('non') && name.includes('veg');
        });

        // Only show non-veg filter if at least one product has the non-veg tag
        const hasNonVegProducts = nonVegTagDef && categories.some(cat =>
          cat.products?.some(p =>
            p.tags?.some(tag => tag.id === nonVegTagDef.id)
          )
        );
        const nonVegTag = hasNonVegProducts ? nonVegTagDef : null;

        const otherTags = tags.filter(t => {
          const name = t.name?.toLowerCase() || '';
          return !(name.includes('veg'));
        });

        return (
          <>
            <div
              ref={filterBarRef}
              className={`tag-filters-container ${isFilterSticky ? 'sticky' : ''}`}
              style={isFilterSticky ? { top: hideHeader ? 0 : (searchBarRef.current?.offsetHeight || 0) } : {}}
            >
              {/* Search bar inside filter container when header is hidden */}
              {hideHeader && (
                <div
                  className="search-bar-container embedded"
                  onClick={handleSearchClick}
                >
                  <div className="search-bar">
                    <span key={searchPlaceholder} className="search-text-animated">
                      {searchPlaceholders[searchPlaceholder]}
                    </span>
                  </div>
                </div>
              )}
              <div className="tag-filters-scroll">
                {/* Veg Toggle Switch - only show if vegTag exists */}
                {vegTag && (
                  <div className={`diet-toggle-container diet-toggle-single ${selectedTags.includes(vegTag.id) ? 'active' : ''}`}>
                    {selectedTags.includes(vegTag.id) && (
                      <button
                        className="diet-clear-btn"
                        onClick={() => {
                          setSelectedTags(prev => prev.filter(id => id !== vegTag.id));
                        }}
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                    <button
                      className={`diet-toggle-option ${selectedTags.includes(vegTag.id) ? 'active' : ''}`}
                      onClick={() => handleTagToggle(vegTag.id)}
                    >
                      {vegTag.categoryImage && (
                        <img
                          src={vegTag.categoryImage}
                          alt={vegTag.name}
                          className="diet-icon"
                        />
                      )}
                      <span className="diet-label">{vegTag.name || 'Veg'}</span>
                    </button>
                  </div>
                )}

                {/* Non-Veg Toggle Switch - only show if nonVegTag exists */}
                {nonVegTag && (
                  <div className={`diet-toggle-container diet-toggle-single ${selectedTags.includes(nonVegTag.id) ? 'active' : ''}`}>
                    {selectedTags.includes(nonVegTag.id) && (
                      <button
                        className="diet-clear-btn"
                        onClick={() => {
                          setSelectedTags(prev => prev.filter(id => id !== nonVegTag.id));
                        }}
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                    <button
                      className={`diet-toggle-option ${selectedTags.includes(nonVegTag.id) ? 'active' : ''}`}
                      onClick={() => handleTagToggle(nonVegTag.id)}
                    >
                      {nonVegTag.categoryImage && (
                        <img
                          src={nonVegTag.categoryImage}
                          alt={nonVegTag.name}
                          className="diet-icon"
                        />
                      )}
                      <span className="diet-label">{nonVegTag.name || 'Non-Veg'}</span>
                    </button>
                  </div>
                )}

                {/* Other Tag Filters */}
                {otherTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id);
                  const isSpecialTag = tag.id === '6932636dec4624b7afca0266';
                  // Use white stars image when special tag is selected, otherwise use default image
                  const imageSrc = isSpecialTag && isSelected 
                    ? 'https://cdn.bluetokaicoffee.com/tags/WhiteStars.png'
                    : tag.categoryImage;
                  
                  return (
                    <button
                      key={tag.id}
                      className={`tag-filter-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {imageSrc && (
                        <img
                          src={imageSrc}
                          alt={tag.name}
                          className="tag-icon"
                        />
                      )}
                      <span className="tag-name">{tag.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {isFilterSticky && <div className="filter-spacer"></div>}
          </>
        );
      })()}
      <hr/>
      {isStoreClosed && (
        <div className="store-closed-notice" role="status">
          <span>This outlet is currently closed. Ordering opens at {formatStoreTime(branchHours.startTime)}.</span>
          <button
            type="button"
            className="store-hours-refresh-button"
            onClick={handleRefreshBranchHours}
            disabled={isRefreshingHours}
          >
            <RefreshIcon className="store-hours-refresh-icon" fontSize="small" aria-hidden="true" />
            {isRefreshingHours ? "Checking…" : "Refresh"}
          </button>
        </div>
      )}
      <div className="main-menu-container">
      {/* Categories */}
      {filteredCategories.length > 0 ? (
        filteredCategories.map((cat) => (
          <div key={cat.id} ref={(el) => (sectionRefs.current[cat.id] = el)}>
            <CategorySection
              category={cat}
              tags={tags}
              onProductClick={handleProductClick}
              onAddToCart={isViewOnly ? null : handleAddToCart}
              onDecreaseFromCart={isViewOnly ? null : decreaseFromCart}
              getProductCartQty={getProductCartQty}
              getProductCartLineCount={getProductCartLineCount}
              subcategoryRefs={subcategoryRefs}
              highlightText={highlightText}
              searchTerm={searchTerm}
              soldOutItems={soldOutItems}
              isViewOnly={isViewOnly}
            />
          </div>
        ))
      ) : (
        <p className="no-products-message">No products found</p>
      )}
      </div>

      {/* Modern ordering UI: floating cart bar + cart drawer + checkout.
          Hidden entirely in view-only mode. */}
      {!isViewOnly && <CartSheet onViewCart={() => setShowCartPage(true)} />}

      {/* Floating Menu button (shown when menu is closed) */}
      {!mobileMenuOpen && (
        <button
          className={`floating-menu-btn ${(cartItemCount > 0 && !isViewOnly) ? "with-cart-bar" : ""}`}
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="floating-menu-label">Menu</span>
        </button>
      )}

      {mobileMenuOpen && (
        <>
          <div className="fullscreen-menu" onClick={() => setMobileMenuOpen(false)}>
            <div className="fullscreen-menu-content" onClick={(e) => e.stopPropagation()}>
              {filteredCategories.map((cat) => (
                <div key={cat.id} className="menu-category-item">
                  <button
                    className="menu-category-btn"
                    onClick={() => {
                      scrollToCategory(cat.id);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className="category-name">{cat.parentName || "Unnamed Category"}</span>
                    {cat.subCategories && cat.subCategories.length > 0 && (
                      <span className="accordion-icon" onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(cat.id);
                      }}>
                        {expandedCategories[cat.id] ? '−' : '+'}
                      </span>
                    )}
                  </button>
                  {expandedCategories[cat.id] && cat.subCategories && cat.subCategories.length > 0 && (
                    <div className="subcategories-list">
                      {cat.subCategories.map((subCat) => (
                        <button
                          key={`${cat.id}-${subCat.name}`}
                          className="menu-subcategory-btn"
                          onClick={() => {
                            scrollToCategory(cat.id, subCat.name);
                            setMobileMenuOpen(false);
                          }}
                        >
                          {subCat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              className={`floating-menu-btn menu-open ${(cartItemCount > 0 && !isViewOnly) ? "with-cart-bar" : ""}`}
              onClick={(e) => { e.stopPropagation(); setMobileMenuOpen(false); }}
            >
              <span className="floating-menu-label">Close</span>
            </button>
          </div>
        </>
      )}

      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={closeModal}
          onAddToCart={isViewOnly ? null : handleAddToCart}
          onReplaceCartItem={isViewOnly ? null : replaceCartItem}
          onAdjustCartSelection={isViewOnly ? null : adjustCartSelectionQuantity}
          getCartQtyForSelection={getCartQtyForSelection}
          soldOutItems={soldOutItems}
          compactMode={openMode === 'add'}
          initialCartItem={initialCartItem}
          isViewOnly={isViewOnly}
        />
      )}

      {customizationProduct && getCartItemsForProduct(customizationProduct).length > 0 && (
        <CustomizationChooserSheet
          product={customizationProduct}
          cartItems={getCartItemsForProduct(customizationProduct)}
          onClose={closeCustomizationChooser}
          onAddNew={handleAddNewCustomization}
          onEdit={handleEditCustomization}
          onUpdateQuantity={updateQuantity}
        />
      )}

      {showCartPage && (
        <CartPage
          onBack={() => setShowCartPage(false)}
          onEditItem={handleEditCartItem}
          canEditItem={canEditCartItem}
        />
      )}

    </div>
  );
}
